// Merge tags for the footnote text editor.

import { groupFieldsByName } from "../fieldGroupSpans";
import { getNameDisplayText } from "../fieldsMapper";
import type { QueryField } from "../types";

/** A query field the footnote text can reference, reduced to what the merge-tag
 * menu needs: the label to show and the token to insert. */
export type FootnoteMergeTagSource = {
  /** The field's name as the UI shows it elsewhere. */
  name: string;
  /** Display variant from the fields grid (1, 2, 3… per alias) — independent
   * of the tag's backend-issued numeric suffix. */
  variant: number;
  /** Canonical merge-field token, e.g. `{d.commitment_1}`. */
  mergeField: string;
};

export type FootnoteMergeTag = {
  /** Friendly label for the picker/autocomplete menu, unique across tags. */
  label: string;
  /** Canonical persisted + displayed tag, e.g. `{d.commitment_1}`. */
  mergeField: string;
};

/** Matches canonical merge tags inside footnote text. */
const CANONICAL_TAG_RE = /\{d\.[a-z][a-z0-9_]{0,62}\}/g;

/** Braces would break the menu label, so they can't appear in it. Field aliases
 * shouldn't contain them, but a label is derived data — sanitize rather than
 * trust. */
const sanitizeLabel = (value: string): string => value.replace(/[{}]/g, "").trim();

/** The bare token the mergetags plugin wraps in its prefix/suffix — the
 * canonical tag with its `{`…`}` stripped, e.g. `d.commitment_1`. */
export const mergeFieldValue = (mergeField: string): string => mergeField.slice(1, -1);

/** Reduce the query's fields to merge-tag sources for the footnote editor.
 *
 * The menu is a second view of the fields grid, so it borrows how the grid
 * presents a field: same grouping of a field's variants, same display name.
 * Reading the alias straight off the row instead would name a primary field
 * `legal_name` and strand a late variant at the bottom of the list.
 *
 * A field the backend hasn't allocated a tag for yet — one added in this
 * editing session — is left out: there is no token to insert, and offering it
 * would put a bare `{}` in the footnote text, which no validation rejects.
 * It becomes referenceable once the query is saved and the tag comes back.
 */
export const toFootnoteMergeTagSources = (
  fields: ReadonlyArray<QueryField>,
): FootnoteMergeTagSource[] =>
  groupFieldsByName(fields)
    .filter((field) => field.mergeField.trim() !== "")
    .map((field) => ({
      name: getNameDisplayText(field),
      variant: field.variant,
      mergeField: field.mergeField,
    }));

/** Build the editor's merge tags from the query's field options. Labels are the
 * field alias; fields sharing an alias (variants) are disambiguated with the
 * grid's display variant. Labels are cosmetic (menu display only) — never an
 * identity, so two aliases that collapse to the same label are disambiguated
 * with the unique merge tag rather than dropped, keeping every field insertable.
 * Field identity always flows through `mergeField`. */
export const buildFootnoteMergeTags = (
  options: ReadonlyArray<FootnoteMergeTagSource>,
): FootnoteMergeTag[] => {
  const aliasCounts = new Map<string, number>();
  for (const option of options) {
    aliasCounts.set(option.name, (aliasCounts.get(option.name) ?? 0) + 1);
  }

  const tags: FootnoteMergeTag[] = [];
  const seenLabels = new Set<string>();
  for (const option of options) {
    const base = sanitizeLabel(option.name) || mergeFieldValue(option.mergeField);
    let label = base;
    if ((aliasCounts.get(option.name) ?? 0) > 1) {
      label = `${base} (${option.variant})`;
    }
    if (seenLabels.has(label)) {
      label = `${label} (${mergeFieldValue(option.mergeField)})`;
    }
    seenLabels.add(label);
    tags.push({ label, mergeField: option.mergeField });
  }
  return tags;
};

/** Canonical `{d.*}` tokens in `text` that aren't in `knownMergeFields` — i.e.
 * references to data fields no longer on the query. */
export const findUnknownMergeFields = (
  text: string,
  knownMergeFields: ReadonlySet<string>,
): string[] => {
  const matches = text.match(CANONICAL_TAG_RE) ?? [];
  return [...new Set(matches.filter((token) => !knownMergeFields.has(token)))];
};
